import { makeStyles } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

const useStyles = makeStyles({
  root: {
    width: "100%",
    height: "70px",
    color: BRAND.pillarOrange,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
});

type HeroKind =
  | "fahrzeugart"
  | "kennzeichen"
  | "marke_modell"
  | "aufbauten"
  | "wert";

const SvgBox = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="220"
    height="70"
    viewBox="0 0 220 70"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

/** Vehicle fan: PKW, Lieferwagen, LKW, Motorrad arrayed across. */
const FahrzeugartHero = () => (
  <SvgBox>
    {/* Krad */}
    <g transform="translate(8 32)">
      <circle cx="6" cy="14" r="6" />
      <circle cx="30" cy="14" r="6" />
      <path d="M10 12l8-8h6l3 5h6" />
    </g>
    {/* PKW */}
    <g transform="translate(56 18)">
      <path d="M2 22l3-9a3 3 0 0 1 2.7-2h26a3 3 0 0 1 2.7 2l3 9z" />
      <path d="M0 22h40v6H0z" />
      <circle cx="9" cy="26" r="2.4" />
      <circle cx="31" cy="26" r="2.4" />
    </g>
    {/* Lieferwagen */}
    <g transform="translate(108 14)">
      <path d="M0 8h22v18H0z" />
      <path d="M22 11h9l5 7v8H22z" />
      <circle cx="8" cy="28" r="2.4" />
      <circle cx="30" cy="28" r="2.4" />
    </g>
    {/* LKW */}
    <g transform="translate(160 8)">
      <path d="M0 8h20v18H0z" />
      <path d="M20 13h10l8 7v6H20z" />
      <circle cx="6" cy="28" r="2.4" />
      <circle cx="30" cy="28" r="2.4" />
      <circle cx="36" cy="28" r="2.4" />
    </g>
  </SvgBox>
);

/** Plate + magnifier */
const KennzeichenHero = () => (
  <SvgBox>
    <g transform="translate(12 14)">
      <rect x="0" y="0" width="120" height="40" rx="6" stroke="currentColor" />
      <rect x="0" y="0" width="22" height="40" rx="6" fill="currentColor" opacity="0.12" />
      <text
        x="65"
        y="28"
        textAnchor="middle"
        fontFamily="Helvetica, Arial, sans-serif"
        fontSize="20"
        fontWeight="800"
        fill="currentColor"
        stroke="none"
      >
        AC-XP
      </text>
    </g>
    {/* Magnifier */}
    <g transform="translate(150 18)">
      <circle cx="20" cy="20" r="14" />
      <path d="M30 30l10 10" />
    </g>
  </SvgBox>
);

/** Marke / Modell — wordmark grid hint */
const MarkeModellHero = () => (
  <SvgBox>
    {Array.from({ length: 8 }).map((_, i) => {
      const col = i % 4;
      const row = Math.floor(i / 4);
      return (
        <g key={i} transform={`translate(${10 + col * 52} ${10 + row * 28})`}>
          <rect width="44" height="22" rx="4" />
          <line x1="6" y1="11" x2="38" y2="11" />
        </g>
      );
    })}
  </SvgBox>
);

/** Auf- / Einbauten — toolkit in cargo box */
const AufbautenHero = () => (
  <SvgBox>
    <g transform="translate(12 18)">
      <rect width="80" height="38" rx="3" />
      <path d="M0 19h80" />
      <path d="M20 8h6v8h-6zM54 8h6v8h-6z" />
    </g>
    <g transform="translate(110 22)" stroke="currentColor">
      <path d="M0 22h6V12h-6zM12 22h6V8h-6zM24 22h6V14h-6zM36 22h6V6h-6z" />
    </g>
    <g transform="translate(170 28)">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 4v8l5 3" />
    </g>
  </SvgBox>
);

/** Wert — Eurozeichen + Kreis-Diagramm */
const WertHero = () => (
  <SvgBox>
    <g transform="translate(20 14)">
      <circle cx="22" cy="20" r="20" />
      <path d="M30 12a14 14 0 1 0 0 16" />
      <path d="M14 16h14M14 22h14" />
    </g>
    <g transform="translate(96 14)">
      <rect width="34" height="40" rx="3" />
      <path d="M6 12h22M6 20h22M6 28h14" />
    </g>
    <g transform="translate(150 14)">
      <path d="M0 40h60" />
      <rect x="6" y="22" width="10" height="16" />
      <rect x="22" y="14" width="10" height="24" />
      <rect x="38" y="6" width="10" height="32" />
    </g>
  </SvgBox>
);

const Heroes: Record<HeroKind, () => JSX.Element> = {
  fahrzeugart: FahrzeugartHero,
  kennzeichen: KennzeichenHero,
  marke_modell: MarkeModellHero,
  aufbauten: AufbautenHero,
  wert: WertHero,
};

const StepHero = ({ kind }: { kind: HeroKind }) => {
  const styles = useStyles();
  const Cmp = Heroes[kind] ?? Heroes.fahrzeugart;
  return (
    <div className={styles.root}>
      <Cmp />
    </div>
  );
};

export default StepHero;
